import { useMemo, useRef } from 'react';

type DebounceOptions = {
  /** If true: invoke callback on first call, ignore subsequent calls within delay (good for preventing double-click). Default: false (trailing). */
  leading?: boolean;
};

export type DebouncedFn<T extends (...args: never[]) => unknown> = T & {
  /** Cancel any pending trailing invocation and reset the leading guard. */
  cancel: () => void;
};

/**
 * Debounces a callback — useful to prevent double-click or reduce rapid successive calls.
 *
 * @param callback - Function to debounce (e.g. button press handler).
 * @param delay - Wait time in ms. e.g. 300–500 for clicks.
 * @param options.leading - true = invoke on first call, ignore further calls within delay (prevents double-click).
 *
 * @example
 * // Prevent double-click (leading)
 * const handleSubmit = useDebounce(() => submitForm(), 400, { leading: true });
 *
 * @example
 * // Trailing debounce (invoke only after user stops calling for 300ms)
 * const handleSearch = useDebounce((q: string) => search(q), 300);
 */
export function useDebounce<T extends (...args: Parameters<T>) => unknown>(
  callback: T,
  delay: number,
  options: DebounceOptions = {},
): DebouncedFn<T> {
  const { leading = false } = options;
  const callbackRef = useRef(callback);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leadingBlockRef = useRef(false);

  callbackRef.current = callback;

  return useMemo(() => {
    const debounced = ((...args: Parameters<T>) => {
      if (leading) {
        if (leadingBlockRef.current) return;
        leadingBlockRef.current = true;
        callbackRef.current(...args);
        leadingTimeoutRef.current = setTimeout(() => {
          leadingBlockRef.current = false;
          leadingTimeoutRef.current = null;
        }, delay);
        return;
      }

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
        timeoutRef.current = null;
      }, delay);
    }) as DebouncedFn<T>;

    debounced.cancel = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (leadingTimeoutRef.current) {
        clearTimeout(leadingTimeoutRef.current);
        leadingTimeoutRef.current = null;
      }
      leadingBlockRef.current = false;
    };

    return debounced;
  }, [delay, leading]);
}
